import { LegacyAuthAdapter } from './legacy/LegacyAuthAdapter';
import type {
  AuthAccessTokenOptions,
  AuthAdapter,
  AuthLogoutOptions,
  AuthNotice,
  AuthProfileUpdatePayload,
  AuthStartLoginOptions,
  AuthState,
} from './types';

type AuthInitOptions = {
  adapter?: AuthAdapter;
  endpoints?: {
    baseUri?: string;
  };
};

const defaultState: AuthState = {
  loading: true,
  authenticated: false,
  token: null,
  user: null,
};

class AuthClient {
  private adapter: AuthAdapter | null = null;

  init(options: AuthInitOptions = {}) {
    if (this.adapter) {
      throw new Error('Auth client has already been initialized.');
    }

    this.adapter = options.adapter ?? new LegacyAuthAdapter(options.endpoints);
    return this;
  }

  subscribe(listener: () => void) {
    return this.adapter?.subscribe(listener) ?? (() => undefined);
  }

  getState(): AuthState {
    return this.adapter?.getState() ?? defaultState;
  }

  async startLogin(options?: AuthStartLoginOptions) {
    return this.getAdapter().startLogin(options);
  }

  async completeLoginIfNeeded() {
    return this.getAdapter().completeLoginIfNeeded();
  }

  async getAccessToken(options?: AuthAccessTokenOptions) {
    return this.getAdapter().getAccessToken(options);
  }

  async getFreshAccessToken(options?: AuthAccessTokenOptions) {
    return this.getAdapter().getFreshAccessToken(options);
  }

  async logout(options?: AuthLogoutOptions) {
    return this.getAdapter().logout(options);
  }

  isAuthenticated() {
    return this.getAdapter().isAuthenticated();
  }

  async updateProfile(data: AuthProfileUpdatePayload) {
    const adapter = this.getAdapter();

    if (!adapter.updateProfile) {
      throw new Error('Current auth adapter does not support profile updates');
    }

    return adapter.updateProfile(data);
  }

  consumeNotice(): AuthNotice | null {
    return this.getAdapter().consumeNotice?.() ?? null;
  }

  private getAdapter() {
    if (!this.adapter) {
      throw new Error('Auth client has not been initialized.');
    }

    return this.adapter;
  }
}

export default new AuthClient();
